import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Badge } from '../../../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../ui/table';
import { Search, Plus, Edit, Eye, Trash2, Filter, Building2, MapPin, Phone } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import AdminNavbar from '../../../AdminNavbar';
import { clinicService } from '../../../../services';
import { useDataFetching } from '../../../../hooks';
import { getStatusBadge, filterDataBySearch } from '../../../../utils';
import { demoClinics } from '../../../../constants';
import { toast } from 'sonner';

const AdminClinicsList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCity, setFilterCity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Use custom hook for data fetching
  const { data: clinics, isLoading, setData: setClinics } = useDataFetching(
    clinicService.getAllClinics,
    demoClinics,
    { errorMessage: 'Failed to load clinics. Using demo data.' }
  );

  // Filter clinics based on search and filters
  const filteredClinics = useMemo(() => {
    if (!Array.isArray(clinics)) {
      return [];
    }
    
    let filtered = clinics;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(clinic =>
        clinic.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        clinic.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
        clinic.type.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by city
    if (filterCity !== 'all') {
      filtered = filtered.filter(clinic => clinic.city === filterCity);
    }

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(clinic => clinic.status === filterStatus);
    }

    return filtered;
  }, [clinics, searchTerm, filterCity, filterStatus]);

  const getUniqueCities = () => {
    if (!Array.isArray(clinics)) {
      return [];
    }
    const cities = clinics.map(clinic => clinic.city).filter(Boolean);
    return [...new Set(cities)];
  };

  const handleDeleteClinic = async (clinicId) => {
    if (!clinicId) {
      toast.error('Invalid clinic ID');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this clinic?')) {
      try {
        await clinicService.deleteClinic(clinicId);
        if (Array.isArray(clinics)) {
          setClinics(clinics.filter(clinic => (clinic._id || clinic.id) !== clinicId));
        }
        toast.success('Clinic deleted successfully!');
      } catch (error) {
        console.error('Error deleting clinic:', error);
        toast.error('Failed to delete clinic. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavbar />
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Manage Clinics</h1>
            <p className="text-gray-600 mt-2">View and manage all clinic information</p>
          </div>
          <Link to="/admin/clinics/create">
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add New Clinic
            </Button>
          </Link>
        </div>

        {/* Search and Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Search & Filters</CardTitle>
            <CardDescription>Find specific clinics using search and filters</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search clinics..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <Select value={filterCity} onValueChange={setFilterCity}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by city" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  <SelectItem value="Faridabad">Faridabad</SelectItem>
                  <SelectItem value="Delhi">Delhi</SelectItem>
                  <SelectItem value="Gurgaon">Gurgaon</SelectItem>
                  <SelectItem value="Noida">Noida</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm('');
                  setFilterCity('all');
                  setFilterStatus('all');
                }}
              >
                <Filter className="h-4 w-4 mr-2" />
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Clinics Table */}
        <Card>
          <CardHeader>
            <CardTitle>Clinics ({filteredClinics.length})</CardTitle>
            <CardDescription>
              Showing {filteredClinics.length} of {Array.isArray(clinics) ? clinics.length : 0} clinics
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Clinic</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Doctors</TableHead>
                    <TableHead>Rating</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClinics.map((clinic) => {
                    const clinicId = clinic._id || clinic.id;
                    return (
                      <TableRow key={clinicId}>
                        <TableCell>
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <Building2 className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                              <div className="font-medium">{clinic.name}</div>
                              <div className="text-sm text-gray-500 flex items-center">
                                <Phone className="h-3 w-3 mr-1" />
                                {clinic.phone}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {clinic.type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center">
                            <MapPin className="h-4 w-4 text-gray-500 mr-1" />
                            {clinic.city}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center">
                            <span className="font-medium">{clinic.doctors}</span>
                            <span className="text-gray-500 ml-1">doctors</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center">
                            <span className="text-yellow-500">★</span>
                            <span className="ml-1 font-medium">{clinic.rating}</span>
                            <span className="text-gray-500 ml-1">({clinic.totalReviews})</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={clinic.status === 'active' ? "default" : "destructive"}>
                            {clinic.status.charAt(0).toUpperCase() + clinic.status.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex space-x-2">
                            <Link to={`/admin/clinics/${clinicId}`}>
                              <Button size="sm" variant="outline">
                                <Eye className="h-4 w-4" />
                              </Button>
                            </Link>
                            <Link to={`/admin/clinics/${clinicId}/edit`}>
                              <Button size="sm" variant="outline">
                                <Edit className="h-4 w-4" />
                              </Button>
                            </Link>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteClinic(clinicId)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {filteredClinics.length === 0 && !isLoading && (
              <div className="text-center py-8">
                <p className="text-gray-500">
                  {searchTerm || filterCity !== 'all' || filterStatus !== 'all' 
                    ? "No clinics found matching your criteria." 
                    : "No clinics available at the moment."}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Clinic Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Clinics</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{Array.isArray(clinics) ? clinics.length : 0}</div>
              <p className="text-xs text-muted-foreground">
                Across all cities
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Clinics</CardTitle>
              <div className="h-4 w-4 rounded-full bg-green-500"></div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {Array.isArray(clinics) ? clinics.filter(c => c.status === 'active').length : 0}
              </div>
              <p className="text-xs text-muted-foreground">
                Currently operational
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Doctors</CardTitle>
              <div className="h-4 w-4 rounded-full bg-blue-500"></div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {Array.isArray(clinics) ? clinics.reduce((sum, clinic) => sum + clinic.doctors, 0) : 0}
              </div>
              <p className="text-xs text-muted-foreground">
                Across all clinics
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Rating</CardTitle>
              <div className="h-4 w-4 rounded-full bg-yellow-500"></div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {Array.isArray(clinics) && clinics.length > 0 
                  ? (clinics.reduce((sum, clinic) => sum + clinic.rating, 0) / clinics.length).toFixed(1)
                  : '0.0'
                }
              </div>
              <p className="text-xs text-muted-foreground">
                Overall clinic rating
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminClinicsList;
